import { useState, useEffect } from "react";
import { Loader2, User2, Zap } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";

export default function ProfileTab() {
  const { user, onboardUser } = useAuth();

  const [name, setName] = useState("");
  const [company, setCompany] = useState("");
  const [phone, setPhone] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setCompany(user.company || "");
      setPhone(user.phone || "");
    }
  }, [user]);

  const getInitials = () => {
    if (user?.name) {
      const parts = user.name.trim().split(/\s+/);
      if (parts.length >= 2) {
        return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
      }
      return parts[0].substring(0, 2).toUpperCase();
    }
    return user?.email?.[0]?.toUpperCase() || "?";
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast.error("Name is required");
      return;
    }
    setIsLoading(true);
    try {
      await onboardUser({ name, company, phone });
      toast.success("Profile updated");
    } catch {
      toast.error("Failed to save profile");
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="flex flex-col gap-6 p-1" style={{ color: "var(--sdb-text)" }}>
      {/* Avatar + email */}
      <div className="flex flex-col items-center gap-3 pt-2">
        <div className="relative flex h-10 w-10 items-center justify-center rounded-sm overflow-hidden">
          <div className="absolute inset-0" style={{ backgroundColor: "var(--sdb-accent)", opacity: 0.15 }} />
          <span className="relative text-sm font-semibold" style={{ color: "var(--sdb-accent)" }}>
            {getInitials()}
          </span>
        </div>

        <span className="font-mono text-sm" style={{ color: "var(--sdb-text-muted)" }}>
          {user.email}
        </span>

        <div className="flex gap-2 mt-1">
          <Badge
            variant="outline"
            className="rounded-sm gap-1.5 font-normal"
            style={{ borderColor: "var(--sdb-border)", color: "var(--sdb-text-secondary)" }}
          >
            <User2 size={12} strokeWidth={1.5} />
            {user.total_generations || 0} generations
          </Badge>
          <Badge
            variant="outline"
            className="rounded-sm gap-1.5 font-normal"
            style={{ borderColor: "var(--sdb-border)", color: "var(--sdb-text-secondary)" }}
          >
            <Zap size={12} strokeWidth={1.5} />
            {user.is_paid ? `$${(user.credits_balance_cents / 100).toFixed(2)} credits` : "Free tier"}
          </Badge>
        </div>
      </div>

      <div className="h-[1px] w-full" style={{ backgroundColor: "var(--sdb-border)" }} />

      {/* Form */}
      <div className="flex flex-col gap-4">
        <div className="font-mono text-[10px] tracking-[2px] uppercase" style={{ color: "var(--sdb-text-subtle)" }}>
          Profile
        </div>
        <Input
          placeholder="Full Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="rounded-sm h-9 bg-transparent"
          style={{ borderColor: "var(--sdb-border)", color: "var(--sdb-text)" }}
        />
        <Input
          placeholder="Company"
          value={company}
          onChange={(e) => setCompany(e.target.value)}
          className="rounded-sm h-9 bg-transparent"
          style={{ borderColor: "var(--sdb-border)", color: "var(--sdb-text)" }}
        />
        <Input
          placeholder="Phone Number"
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          className="rounded-sm h-9 bg-transparent"
          style={{ borderColor: "var(--sdb-border)", color: "var(--sdb-text)" }}
        />
      </div>

      <Button
        onClick={handleSave}
        disabled={isLoading}
        className="w-full rounded-sm gradient-bg glow-btn text-black hover:text-black font-medium"
      >
        {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" strokeWidth={1.5} /> : "Save Profile"}
      </Button>
    </div>
  );
}
